import { ErrorInfo, Fragment, memo, Suspense, useCallback, useState } from 'react';
import { Error } from 'react-admin';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import SkeletonBox from '../generic/SkeletonBox';

/**
 * ErrorBoundaryWithSuspense
 * Wrapper for the common pattern:
 * ErrorBoundary + Suspense for lazy loaded components
 *
 * Props:
 * - children: the subtree to protect
 * - loadingText: string shown in SkeletonBox fallback
 * - fullSize: if true SkeletonBox uses width/height 100%
 *
 * Behavior:
 * - Catches render errors in children and shows <Error/>
 * - Shows SkeletonBox during lazy load (Suspense fallback)
 */

export type ErrorBoundaryWithSuspenseProps = {
  children?: React.ReactNode;
  customFallback?: React.ReactNode | null;
  loadingText?: string;
  fullSize?: boolean;
};

export const ErrorBoundaryWithSuspense = memo(
  ({
    children,
    loadingText,
    fullSize = true,
    customFallback,
  }: ErrorBoundaryWithSuspenseProps) => {
    // state to hold error info for error boundary
    const [errorInfo, setErrorInfo] = useState<ErrorInfo>();

    // react-error-boundary onError signature
    const handleError = useCallback((error: unknown, info: ErrorInfo) => {
      setErrorInfo(info);
    }, []);

    return (
      <ErrorBoundary
        onError={handleError}
        fallbackRender={({ error, resetErrorBoundary }) => (
          <Error
            error={error as Error}
            errorInfo={errorInfo}
            resetErrorBoundary={resetErrorBoundary}
          />
        )}
      >
        <Suspense
          fallback={
            <SuspenseFallback
              customFallback={customFallback}
              loadingText={loadingText}
              fullSize={fullSize}
            />
          }
        >
          {children}
        </Suspense>
      </ErrorBoundary>
    );
  },
);

ErrorBoundaryWithSuspense.displayName = 'ErrorBoundaryWithSuspense';

/**
 * SuspenseFallback
 * Internal component that handles fallback logic for Suspense
 * Null is a valid customFallback to show nothing
 */
type SuspenseFallbackProps = {
  customFallback?: React.ReactNode | null;
  loadingText?: string;
  fullSize?: boolean;
};

const SuspenseFallback = memo(
  ({ customFallback, loadingText, fullSize }: SuspenseFallbackProps) => {
    const { t } = useTranslation();
    const strLoading = t('blox.general.loading');

    if (customFallback !== undefined) {
      return <Fragment key="CustomSuspenseFallback">{customFallback}</Fragment>;
    }

    if (customFallback === null) {
      return null;
    }

    return (
      <SkeletonBox
        text={loadingText ?? strLoading}
        width={fullSize ? '100%' : undefined}
        height={fullSize ? '100%' : undefined}
      />
    );
  },
);

SuspenseFallback.displayName = 'SuspenseFallback';
